'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';

interface LoginFormProps {
  onLoginSuccess?: (userData: { email: string; name: string }) => void;
}

interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

const LoginForm = ({ onLoginSuccess }: LoginFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [lastAttemptTime, setLastAttemptTime] = useState<number>(0);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const router = useRouter();
  const { signIn } = useAuth();
  const cooldownRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (cooldownSeconds <= 0) {
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current);
        cooldownRef.current = null;
      }
      return;
    }
    if (cooldownRef.current) return; // already running
    cooldownRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) {
            clearInterval(cooldownRef.current);
            cooldownRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (cooldownRef.current) {
        clearInterval(cooldownRef.current);
        cooldownRef.current = null;
      }
    };
  }, [cooldownSeconds > 0]);

  const mockCredentials = [
    { email: 'vip@ibdaacraft.com', password: 'VIP2026!', name: 'أحمد محمد', type: 'VIP' },
    { email: 'family@ibdaacraft.com', password: 'Family2026!', name: 'فاطمة علي', type: 'Family' },
  ];

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t('البريد الإلكتروني مطلوب', 'Email is required');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('البريد الإلكتروني غير صحيح', 'Invalid email address');
    }

    if (!formData.password.trim()) {
      newErrors.password = t('كلمة المرور مطلوبة', 'Password is required');
    } else if (formData.password.length < 6) {
      newErrors.password = t('كلمة المرور يجب أن تكون 6 أحرف على الأقل', 'Password must be at least 6 characters');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Client-side rate limiting: max 5 attempts per 5 minutes
    const now = Date.now();
    const WINDOW_MS = 300_000;
    const MAX_ATTEMPTS = 5;

    if (attemptCount >= MAX_ATTEMPTS && now - lastAttemptTime < WINDOW_MS) {
      const remaining = Math.ceil((WINDOW_MS - (now - lastAttemptTime)) / 1000);
      setCooldownSeconds(remaining);
      setErrors({ general: t(`تم تجاوز عدد المحاولات المسموح بها. يرجى الانتظار ${remaining} ثانية ثم المحاولة مرة أخرى.`, `Too many attempts. Please wait ${remaining} seconds and try again.`) });
      return;
    }

    // Reset counter if window has passed
    if (now - lastAttemptTime >= WINDOW_MS) {
      setAttemptCount(0);
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Use real Supabase authentication
      const { user } = await signIn(formData.email, formData.password);

      if (user) {
        if (formData.rememberMe && isHydrated) {
          localStorage.setItem('rememberedEmail', formData.email);
        }
        
        if (onLoginSuccess) {
          onLoginSuccess({ email: user.email || formData.email, name: user.user_metadata?.full_name || 'User' });
        }
        
        router.replace('/customer-dashboard');
      }
    } catch (error: any) {
      console.error('Login error:', error);

      // Increment attempt counter on failure
      setAttemptCount((prev) => prev + 1);
      setLastAttemptTime(Date.now());
      
      let errorMessage = t('حدث خطأ أثناء تسجيل الدخول. يرجى المحاولة مرة أخرى.', 'An error occurred during login. Please try again.');
      
      const msg = error?.message?.toLowerCase() || '';
      if (msg.includes('invalid login credentials') || msg.includes('invalid credentials')) {
        errorMessage = t('البريد الإلكتروني أو كلمة المرور غير صحيحة.', 'Incorrect email or password.');
      } else if (msg.includes('email not confirmed')) {
        errorMessage = t('يرجى تأكيد بريدك الإلكتروني أولاً قبل تسجيل الدخول.', 'Please confirm your email first before logging in.');
      } else if (msg.includes('rate limit') || msg.includes('too many requests') || msg.includes('request rate limit')) {
        // Extract wait time from Supabase error if present, otherwise default to 60s
        const waitMatch = error?.message?.match(/(\d+)\s*second/i);
        const waitSeconds = waitMatch ? parseInt(waitMatch[1], 10) : 60;
        setCooldownSeconds(waitSeconds);
        // Reset attempt counter so user can try again after cooldown
        setAttemptCount(0);
        errorMessage = t(`تم تجاوز الحد المسموح به من المحاولات. يرجى الانتظار ${waitSeconds} ثانية ثم المحاولة مرة أخرى.`, `Too many attempts. Please wait ${waitSeconds} seconds and try again.`);
      } else if (msg.includes('network') || msg.includes('fetch')) {
        errorMessage = t('خطأ في الاتصال بالشبكة. يرجى التحقق من اتصالك بالإنترنت.', 'Network error. Please check your internet connection.');
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isHydrated) {
    return (
      <div className="w-full max-w-md mx-auto bg-card rounded-lg shadow-lg p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-3/4 mx-auto"></div>
          <div className="space-y-4">
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
            <div className="h-12 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto bg-card rounded-lg shadow-lg p-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Icon name="UserCircleIcon" size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground font-amiri mb-2">
          {t('تسجيل الدخول', 'Sign In')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('أدخل بياناتك للوصول إلى حسابك', 'Enter your credentials to access your account')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {errors.general && (
          <div className="flex items-start gap-3 p-4 rounded-md bg-error/10 border border-error/20">
            <Icon name="ExclamationCircleIcon" size={20} className="text-error flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error">{errors.general}</p>
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-foreground">
            {t('البريد الإلكتروني', 'Email Address')}
          </label>
          <div className="relative">
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 pr-12 rounded-md border ${
                errors.email ? 'border-error' : 'border-input'
              } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
              placeholder="example@ibdaacraft.com"
              disabled={isLoading}
            />
            <Icon name="EnvelopeIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          </div>
          {errors.email && (
            <p className="text-sm text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={16} />
              {errors.email}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-foreground">
            {t('كلمة المرور', 'Password')}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 pr-12 rounded-md border ${
                errors.password ? 'border-error' : 'border-input'
              } bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-premium`}
              placeholder={t('أدخل كلمة المرور', 'Enter your password')}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-premium"
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} />
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-error flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={16} />
              {errors.password}
            </p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="rememberMe"
              checked={formData.rememberMe}
              onChange={handleInputChange}
              className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
              disabled={isLoading}
            />
            <span className="text-sm text-foreground">{t('تذكرني', 'Remember me')}</span>
          </label>
          <button
            type="button"
            className="text-sm text-primary hover:text-primary/80 transition-premium font-medium"
            disabled={isLoading}
          >
            نسيت كلمة المرور؟
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading || cooldownSeconds > 0}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground font-medium transition-premium hover:bg-primary/90 hover:shadow-md active-press disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
              <span>{t('جاري تسجيل الدخول...', 'Signing in...')}</span>
            </>
          ) : cooldownSeconds > 0 ? (
            <span>{t(`انتظر ${cooldownSeconds} ثانية`, `Wait ${cooldownSeconds} seconds`)}</span>
          ) : (
            <>
              <Icon name="ArrowRightOnRectangleIcon" size={20} />
              <span>{t('تسجيل الدخول', 'Sign In')}</span>
            </>
          )}
        </button>

        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            {t('ليس لديك حساب؟', "Don't have an account?")}{' '}
            <a href="/subscription-cards" className="text-primary hover:text-primary/80 font-medium transition-premium">
              {t('اشترك الآن', 'Subscribe Now')}
            </a>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;